/**
 * User Profile Page
 *
 * View and edit user profile, change password, manage preferences.
 */

import {
  Typography,
  Card,
  Space,
  Avatar,
  Row,
  Col,
  Descriptions,
  Tag,
} from 'antd';
import { useAuthStore } from '@store/authStore';

const { Title, Text } = Typography;

export default function ProfilePage() {
  const { user, tenant } = useAuthStore();

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (id: string) => {
    const colors = [
      '#f56a00',
      '#7265e6',
      '#ffbf00',
      '#00a2ae',
      '#1890ff',
      '#eb2f96',
      '#52c41a',
      '#722ed1',
    ];
    const index = id.charCodeAt(0) % colors.length;
    return colors[index];
  };

  if (!user) {
    return null;
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Title level={3}>Profile</Title>

      <Row gutter={24}>
        <Col xs={24} md={8}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Avatar
                size={100}
                style={{ backgroundColor: getAvatarColor(user.id), fontSize: 36 }}
              >
                {getInitials(user.fullName)}
              </Avatar>
              <Title level={4} style={{ marginTop: 16, marginBottom: 4 }}>
                {user.fullName}
              </Title>
              <Text type="secondary">{user.email}</Text>
              <div style={{ marginTop: 12 }}>
                {user.isSuperAdmin && (
                  <Tag color="gold">Super Admin</Tag>
                )}
                {tenant && (
                  <Tag color="blue">{tenant.roleName}</Tag>
                )}
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={16}>
          <Card title="Account Information">
            <Descriptions column={1} labelStyle={{ width: 150 }}>
              <Descriptions.Item label="Full Name">
                {user.fullName}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                <Space>
                  {user.email}
                  {user.emailVerifiedAt ? (
                    <Tag color="success">Verified</Tag>
                  ) : (
                    <Tag color="warning">Not Verified</Tag>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Last Login">
                {user.lastLoginAt
                  ? new Date(user.lastLoginAt).toLocaleString()
                  : 'Never'}
              </Descriptions.Item>
              <Descriptions.Item label="Account Type">
                {user.isSuperAdmin ? 'Super Administrator' : 'Regular User'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {tenant && (
            <Card title="Organization" style={{ marginTop: 16 }}>
              <Descriptions column={1} labelStyle={{ width: 150 }}>
                <Descriptions.Item label="Organization">
                  {tenant.tenantName}
                </Descriptions.Item>
                <Descriptions.Item label="Role">
                  <Tag color="blue">{tenant.roleName}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Permissions">
                  <Space wrap>
                    {tenant.permissions.slice(0, 5).map((perm) => (
                      <Tag key={perm}>{perm}</Tag>
                    ))}
                    {tenant.permissions.length > 5 && (
                      <Tag>+{tenant.permissions.length - 5} more</Tag>
                    )}
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          <Card title="Security" style={{ marginTop: 16 }}>
            <Text type="secondary">
              Sign-in and session security are managed by Cloudflare Access.
            </Text>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
